
import React, { useState } from 'react';
import { toast } from 'sonner';
import { createStripeCheckout } from '@/utils/stripeUtils';
import { trackConversion, getReferralId } from '@/utils/rewardfulUtils';

interface UseStripeCheckoutProps {
  email: string;
  name: string;
  addEmailToMailerLite: (email: string) => Promise<void>;
  setPaymentError: (error: string) => void;
}

export const useStripeCheckout = ({
  email,
  name,
  addEmailToMailerLite,
  setPaymentError
}: UseStripeCheckoutProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const saveStateSecurely = (email: string, name: string) => {
    const checkoutState = {
      email,
      name,
      timestamp: Date.now(),
      step: 'checkout_initiated',
      userAgent: navigator.userAgent
    };
    
    console.log("[STRIPE CHECKOUT] Guardando estado de forma segura:", checkoutState);
    
    const stateString = JSON.stringify(checkoutState);
    
    // Guardar en múltiples ubicaciones
    try {
      localStorage.setItem('checkout_state', stateString);
      localStorage.setItem('checkout_state_backup', stateString);
      console.log("[STRIPE CHECKOUT] ✓ Estado guardado en localStorage");
    } catch (e) {
      console.error("[STRIPE CHECKOUT] ✗ Error guardando en localStorage:", e);
    }
    
    try {
      sessionStorage.setItem('checkout_state', stateString);
      sessionStorage.setItem('checkout_state_backup', stateString);
      console.log("[STRIPE CHECKOUT] ✓ Estado guardado en sessionStorage");
    } catch (e) {
      console.error("[STRIPE CHECKOUT] ✗ Error guardando en sessionStorage:", e);
    }
    
    if (email) {
      try {
        localStorage.setItem('pending_email', email);
        sessionStorage.setItem('pending_email', email);
        console.log("[STRIPE CHECKOUT] ✓ Email guardado por separado");
      } catch (e) {
        console.error("[STRIPE CHECKOUT] ✗ Error guardando email:", e);
      }
    }
    
    if (name) {
      try {
        localStorage.setItem('pending_name', name);
        sessionStorage.setItem('pending_name', name);
        console.log("[STRIPE CHECKOUT] ✓ Nombre guardado por separado");
      } catch (e) {
        console.error("[STRIPE CHECKOUT] ✗ Error guardando nombre:", e);
      }
    }
    
    try {
      localStorage.setItem('checkout_in_progress', 'true');
      sessionStorage.setItem('checkout_in_progress', 'true');
      console.log("[STRIPE CHECKOUT] ✓ Flag de checkout en progreso guardado");
    } catch (e) {
      console.error("[STRIPE CHECKOUT] ✗ Error guardando flag:", e);
    }
  };

  const handleSubscribe = async () => {
    try {
      setIsRedirecting(true);
      setPaymentError('');
      const paymentEmail = email || '';
      const paymentName = name || '';
      
      console.log("[STRIPE CHECKOUT] Iniciando proceso de suscripción...");
      console.log("[STRIPE CHECKOUT] Email:", paymentEmail, "Name:", paymentName);
      
      // Detectar descuento de la URL o de referidos
      const urlParams = new URLSearchParams(window.location.search);
      let discountType = urlParams.get('discount');
      
      // Si no hay descuento en URL, verificar si hay descuento por referido
      if (!discountType) {
        const referralDiscount = localStorage.getItem('referral_discount') || sessionStorage.getItem('referral_discount');
        if (referralDiscount) {
          discountType = referralDiscount;
          console.log("[STRIPE CHECKOUT] Applying referral discount:", referralDiscount);
        }
      }
      
      // También verificar si hay un parámetro 'via' en la URL (Rewardful)
      if (!discountType && urlParams.get('via')) {
        discountType = '30'; // Descuento del 30% para referidos de Rewardful
        console.log("[STRIPE CHECKOUT] Detected 'via' parameter, applying 30% discount");
      }
      
      console.log("[STRIPE CHECKOUT] Final discount type:", discountType);
      
      // Guardar estado antes de hacer cualquier cosa
      saveStateSecurely(paymentEmail, paymentName);
      
      // Agregar email a MailerLite si está disponible
      if (email) {
        try {
          await addEmailToMailerLite(email);
          console.log("[STRIPE CHECKOUT] Email agregado a MailerLite");
        } catch (e) {
          console.error("[STRIPE CHECKOUT] Error agregando a MailerLite:", e);
        }
      }
      
      // Crear sesión de checkout
      try {
        const toastId = toast.loading("Conectando con el servidor de pagos...");
        
        let attempts = 0;
        const maxAttempts = 3;
        let lastError = null;
        
        while (attempts < maxAttempts) {
          try {
            console.log(`[STRIPE CHECKOUT] Intento ${attempts + 1} para crear sesión de checkout`);
            
            // Volver a guardar el estado antes de cada intento
            saveStateSecurely(paymentEmail, paymentName);
            
            const checkoutUrl = await createStripeCheckout(paymentEmail, paymentName, discountType);
            
            toast.dismiss(toastId);
            console.log("[STRIPE CHECKOUT] URL de checkout obtenida:", checkoutUrl);
            
            // Guardar una vez más justo antes de redirigir
            saveStateSecurely(paymentEmail, paymentName);
            
            console.log("[STRIPE CHECKOUT] Redirigiendo a Stripe...");
            
            // Si hay un referral ID, rastrear que el usuario está iniciando el checkout
            const referralId = getReferralId();
            if (referralId && paymentEmail) {
              console.log("[REWARDFUL] Iniciando tracking de checkout para referral:", referralId);
              try {
                // Rastrear que el usuario está iniciando el proceso de pago
                trackConversion(paymentEmail, {
                  attempt: attempts + 1,
                  action: 'checkout_initiated',
                  referral_id: referralId,
                  discount_type: discountType
                });
              } catch (trackingError) {
                console.error("[REWARDFUL] Error tracking checkout:", trackingError);
              }
            }
            
            // Redirigir inmediatamente
            window.location.href = checkoutUrl;
            
            return;
          } catch (attemptError) {
            console.error(`[STRIPE CHECKOUT] Intento ${attempts + 1} falló:`, attemptError);
            lastError = attemptError;
            attempts++;
            
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          }
        }
        
        toast.dismiss(toastId);
        throw lastError;
      } catch (error: any) {
        console.error("[STRIPE CHECKOUT] Error creando sesión de checkout:", error);
        
        // Handle specific error messages
        if (error.message && error.message.includes("Edge Function returned a non-2xx status code")) {
          toast.error(
            <div>
              <p>Error en el servidor de pagos.</p>
              <p className="text-sm mt-1">Por favor, intenta nuevamente en unos momentos o contacta a soporte.</p>
            </div>,
            { duration: 7000 }
          );
          setPaymentError('Error en el servidor de pagos. Por favor, intenta nuevamente en unos momentos o contacta a soporte.');
        } else if (error.message && error.message.includes("tiempo de espera excedido")) {
          toast.error(
            <div>
              <p>Tiempo de espera excedido al conectar con el servidor de pagos.</p>
              <p className="text-sm mt-1">Por favor, intenta nuevamente en unos momentos.</p>
            </div>,
            { duration: 5000 }
          );
          setPaymentError('Tiempo de espera excedido al conectar con el servidor de pagos. Por favor, intenta nuevamente en unos momentos.');
        } else {
          toast.error(`Error al iniciar el proceso de pago: ${error.message || "Por favor, inténtalo de nuevo."}`);
          setPaymentError(error.message || "Error al iniciar el proceso de pago. Por favor, inténtalo de nuevo.");
        }
        
        setIsRedirecting(false);
      }
    } catch (error: any) {
      console.error("[STRIPE CHECKOUT] Error final en handleSubscribe:", error);
      
      toast.error(`Error al iniciar el proceso de pago: ${error.message || "Por favor, inténtalo de nuevo."}`);
      setPaymentError(error.message || "Error al iniciar el proceso de pago. Por favor, inténtalo de nuevo.");
      setIsRedirecting(false);
    }
  };

  return { handleSubscribe, isRedirecting };
};
